import React, { useEffect, useState } from 'react'

import { useMutation } from '@apollo/client'

import BuildConfig from '$extensionSrc/buildConfig'
import ActionButton, {
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import {
  extensionDashboardUrl,
  installPath,
} from '$extensionSrc/consts/urlConsts'
import RecommendationDisplayWrapper from '$extensionSrc/recommendations/recommendationDisplayWrapper'
import { getRecommendationsRegistryForPlatform } from '$extensionSrc/recommendations/recommendationRegistryApi'
import {
  FBRecommendationKey,
  PLATFORM,
  TwitterRecommendationKey,
  VenmoRecommendationKey,
} from '$extensionSrc/utils/enums'
import BrowsersImage from '$tenantSrc/browsers-image.svg'

import ManualRecommendationCardPreview from './ManualRecommendationCardPreview'
import { client } from '../apolloClient'
import { requestInstallReminderEmailMutation } from '../data/query'
import { getExtensionUrl } from '../utils/urlUtils'

import './ExtensionRedirectPage.scss'

export default function ExtensionRedirectPage({
  browser,
  extensionId,
  userEmail,
  urlParams = null,
}: {
  browser: 'chrome' | 'firefox' | 'edge'
  extensionId: string
  userEmail: string
  urlParams: { [key: string]: string } | null
}) {
  const [haveSentEmail, setHaveSentEmail] = useState(false)
  const [sendEmail] = useMutation(requestInstallReminderEmailMutation, {
    client,
    variables: { id: null }, // will be inferred from cookies
    ignoreResults: true, // do not re-render afterwards; no data we need
    onCompleted: () => {
      setHaveSentEmail(true)
    },
  })
  const [failedToQueryExtension, setFailedToQueryExtension] = useState(false)

  // Parse url
  const url = new URL(window.location.href)

  const path = url.searchParams.get('path') || extensionDashboardUrl

  const queryParamObj = {
    ...Object.fromEntries(url.searchParams),
    ...urlParams,
  }
  delete queryParamObj.path

  const targetExtensionUrl = getExtensionUrl(
    path,
    browser,
    extensionId,
    queryParamObj,
    window.location.hash,
  )
  const testUrl = getExtensionUrl(extensionDashboardUrl, browser, extensionId)

  useEffect(() => {
    fetch(testUrl, { method: 'HEAD' })
      .then(async () => {
        setTimeout(() => {
          window.location.assign(targetExtensionUrl)
        }, 200)
      })
      .catch(async () => {
        setFailedToQueryExtension(true)
      })
  }, [targetExtensionUrl, testUrl])

  // Set default content
  let content = (
    <div className="extension-redirect-default">
      Redirecting...{' '}
      <a className="link1 darkest-gray" href={targetExtensionUrl}>
        click here if the page does not reload
      </a>
      .
    </div>
  )

  const queryParams = new URLSearchParams()
  queryParams.set('b', browser)
  const installURL = `${installPath}?${queryParams.toString()}`

  // Set alternate content
  let extensionPreview
  if (failedToQueryExtension) {
    content = (
      <div className="hook">
        <BrowsersImage
          className="browsers-image"
          role="img"
          title="Chrome, Firefox, and Safari browser logos"
        />
        <div className="text-wrapper">
          <div className="heading1 bold center">
            Your last step — add {BuildConfig.TENANT_NAME} to your browser
          </div>
          <div className="subheading-desktop">
            <ActionButton
              type={ActionButtonType.PRIMARY}
              role="link"
              onClick={() => window.location.assign(installURL)}
            >
              Install {BuildConfig.TENANT_NAME} now
            </ActionButton>
            <div className="body1 darkest-gray center">
              Already on a desktop browser that has {BuildConfig.TENANT_NAME}?{' '}
              <a className="link1 darkest-gray" href={targetExtensionUrl}>
                Click here if you're not redirected
              </a>
              .<br />
              Want to see your account settings?{' '}
              <a className="link1 darkest-gray" href="/settings/">
                Click here
              </a>
              .
            </div>
          </div>
          <div className="subheading-mobile body1 darkest-gray center">
            <b>Check your email on desktop</b> to install{' '}
            {BuildConfig.TENANT_NAME}. We emailed installation links to you at{' '}
            <b>{userEmail}</b>
          </div>
        </div>
      </div>
    )

    const previewRecommendationKeyPlatforms = {
      [VenmoRecommendationKey.VENMO_FRIENDS_LIST]: PLATFORM.VENMO,
      [TwitterRecommendationKey.TWITTER_LOCATION_ACCESS]: PLATFORM.TWITTER,
      [FBRecommendationKey.FB_OFF_PLATFORM_ACTIVITY]: PLATFORM.FACEBOOK,
    }

    const previewRecommendationSpecs = Object.fromEntries(
      Object.keys(previewRecommendationKeyPlatforms).map((recKey) => {
        const platform = previewRecommendationKeyPlatforms[recKey]
        return [recKey, getRecommendationsRegistryForPlatform(platform)[recKey]]
      }),
    )

    extensionPreview = (
      <div className="extension-preview">
        <div className="preview-divider">
          <div className="divider" />
          <div className="or-text body2 mobile-text">
            Don't have your computer? ↓
          </div>
          <div className="or-text body2 desktop-text">
            Can't install the extension right now? ↓
          </div>
          <div className="divider" />
        </div>
        <div className="heading3 bold">
          ...That’s okay, add {BuildConfig.TENANT_NAME} later
        </div>
        <div className="body1 darkestGray">
          For now, here are 3 privacy recommendations you can start with from
          your phone.
        </div>
        <div className="preview-cards">
          {Object.keys(previewRecommendationKeyPlatforms).map((key) => (
            <ManualRecommendationCardPreview
              key={key}
              platform={previewRecommendationKeyPlatforms[key]}
              spec={
                new RecommendationDisplayWrapper(
                  previewRecommendationSpecs[key],
                  previewRecommendationSpecs[key].concernTags[0],
                )
              }
            />
          ))}
        </div>
      </div>
    )

    if (!haveSentEmail) {
      sendEmail()
    }
  }

  return (
    <div className="ExtensionRedirectPage">
      {content}
      {extensionPreview}
    </div>
  )
}
